import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Department } from 'src/app/models/department.model';
import { Company } from 'src/app/models/company.model';
import { Role } from 'src/app/models/role.model';
import { User } from 'src/app/models/user.model';
import { AddUserDialogComponent } from './add-user-dialog/add-user-dialog.component';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { ApiService, AuthService, PermissionsService, UserService, UtilsService } from 'src/app/services';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix, MatPrefix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { TitleCasePipe } from '@angular/common';
import { UserProfilePictureComponent } from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    RouterLink,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatPrefix,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TitleCasePipe,
    UserProfilePictureComponent,
  ],
})
export class UsersComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private api = inject(ApiService);
  protected fb: UntypedFormBuilder;
  router: Router;
  protected activatedRoute: ActivatedRoute;
  permissions = inject(PermissionsService);
  protected utils: UtilsService;
  private auth = inject(AuthService);
  private userService = inject(UserService);
  dialog = inject(MatDialog);

  private _workers_mode: boolean = false;
  get workers_mode() {
    return this._workers_mode;
  }
  @Input() set workers_mode(val: boolean) {
    this.setWorkersMode(val);
  }

  displayedColumns: string[] = ['image', 'name', 'email', 'role', 'company', 'actions'];
  dataSource: MatTableDataSource<User> = new MatTableDataSource<User>();

  filterForm: UntypedFormGroup;

  roles: Role[] = [];
  departments: Department[] = [];
  departmentCategories: DepartmentCategory[] = [];
  company: Company[] = [];

  protected me: User = null as any;

  override filterFormExtraParams: any = {
    role_id: [
      this.activatedRoute.snapshot.queryParamMap.get('role_id') != null
        ? +(this.activatedRoute.snapshot.queryParamMap.get('role_id') as any)
        : '',
    ],
    company_id: [
      this.activatedRoute.snapshot.queryParamMap.get('company_id') != null
        ? +(this.activatedRoute.snapshot.queryParamMap.get('company_id') as any)
        : '',
    ],
  };

  constructor() {
    const fb = inject(UntypedFormBuilder);
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const utils = inject(UtilsService);

    super(router, fb, activatedRoute, utils);

    this.fb = fb;
    this.router = router;
    this.activatedRoute = activatedRoute;
    this.utils = utils;
  }

  override ngOnInit(): void {
    this.fetchRolesAndDepartments();
    super.ngOnInit();
    this.fetchCompanies();
    this.me = this.userService.getCurrentUser();
  }

  deleteUser(index: number) {
    const user = this.dataSource.data[index];
    if (confirm(`¿Estás seguro que quieres eliminar el usuario "${user.name + ' ' + user.surnames}"?`)) {
      this.api.deleteUser(user.id).subscribe((): void => {
        this.fetchData();
      });
    }
  }

  impersonateUser(index: number) {
    const user = this.dataSource.data[index];
    this.auth.impersonate(user.id).subscribe(() => {
      this.router.navigate(['/']);
    });
  }

  private fetchRolesAndDepartments() {
    this.permissions.rolesObservable.subscribe(data => {
      if (data != null) {
        this.roles = Object.keys(data).map(k => data[k]);
      }
    });

    this.permissions.departmentsObservable.subscribe(data => {
      if (data != null) {
        this.departments = Object.keys(data).map(k => data[k]);
      }
    });

    this.permissions.departmentCategoriesObservable.subscribe(data => {
      if (data != null) {
        this.departmentCategories = Object.keys(data).map(k => data[k]);
      }
    });
  }

  addUser() {
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      width: '750px',
      data: {
        roles: this.roles,
      },
    });

    dialogRef.afterClosed().subscribe((user: User) => {
      if (user != null) {
        this.fetchData();
      }
    });
  }

  override fetchData() {
    const body = {
      ...this.filterForm.value,
      with: 'role,current_working_contract..current_working_contract_variable_condition..department_categories',
    };
    this.api.getUsersPaged(body).subscribe(data => {
      this.dataSource.data = data.data;
      this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);
    });
  }

  private fetchCompanies() {
    this.api.getCompaniesPaged().subscribe(data => {
      if (data != null) {
        this.company = data.data;
      }
    });
  }

  private setWorkersMode(val: boolean) {
    if (val != null) {
      // setegem valor
      this._workers_mode = val;

      if (val) {
        // modifiquem form
        this.filterForm.patchValue({ role_id: 1 });
        // modifiquem columnes
        this.displayedColumns = ['image', 'name', 'email', 'current_working_contract', 'actions'];
      } else {
        // modifiquem form
        this.filterForm.patchValue({ role_id: null });
        // modifiquem columnes
        this.displayedColumns = ['image', 'name', 'email', 'role', 'company', 'actions'];
      }
    }
  }
}
